import React, { useEffect, useMemo } from "react";
import { useImmer } from "use-immer";

import { IgButton, IgTextField, IgTypography } from "@intelligems/ig-theme";
import { GetMetafieldRequestQueryParamType } from "@intelligems/ig-types";
import { ShopifyVariantId } from "@intelligems/ig-utils";
import { Grid, useTheme } from "@mui/material";
import { useDeleteMetafield } from "@src/api/metafields/delete-metafield";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { organizationsKeys } from "@src/api/queryKeys";
import { Json } from "@src/utils/json";
import { useQueryClient } from "@tanstack/react-query";

import { useGetMetafield } from "../../../api/shops/get-metafield";
import { SkeletonGroup } from "../../../components/Utils/SkeletonGroup";

interface MetafieldLookupSectionProps {
  metafieldKey: string;
  type: "product" | "variant";
}

export const MetafieldLookupSection = ({
  metafieldKey,
  type,
}: MetafieldLookupSectionProps) => {
  const [params, setParams] = useImmer<
    Partial<GetMetafieldRequestQueryParamType>
  >({});
  const [id, setId] = useImmer<string>("");
  const org = useGetOrg();
  const theme = useTheme();
  const metafield = useGetMetafield(params);
  const deleteMetafield = useDeleteMetafield();
  const queryClient = useQueryClient();

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    const timer = setTimeout(async () => {
      setParams({ id, key: metafieldKey, type });
    }, 250);
    return () => clearTimeout(timer);
  }, [id]);

  const { el, valid } = useMemo(() => {
    if (metafield.isLoading) {
      return { el: <SkeletonGroup lines={1} />, valid: false };
    }

    if (metafield.isSuccess) {
      if (metafield.data.metafield?.id) {
        if (metafield.data.metafield.jsonValue) {
          return {
            el: (
              <Json
                name={"igTestGroups"}
                data={metafield.data.metafield.jsonValue}
              />
            ),
            valid: true,
          };
        }
        return {
          el: (
            <IgTypography
              variant={"body2"}
              sx={{ color: theme.palette.text.tonal.error, fontWeight: 600 }}
            >
              No metafield found
            </IgTypography>
          ),
          valid: false,
        };
      }
      return {
        el: (
          <IgTypography
            variant={"body2"}
            sx={{ color: theme.palette.text.tonal.error, fontWeight: 600 }}
          >
            No metafield found, or invalid variant id
          </IgTypography>
        ),
        valid: false,
      };
    }
    return {
      el: (
        <IgTypography variant={"body2"} color={theme.palette.text.secondary}>
          Enter a Variant ID to check its metafield
        </IgTypography>
      ),
      valid: false,
    };
  }, [metafield.isSuccess, metafield.isLoading, metafield.data]);

  return (
    <Grid
      container
      direction={"column"}
      item
      sx={{ width: "100%" }}
      rowSpacing={1}
    >
      <Grid item>
        <IgTypography variant="h3" fontWeight={"fontWeightMedium"}>
          Check Variant Metafield
        </IgTypography>
      </Grid>
      <Grid container item direction={"row"} columnSpacing={2}>
        <Grid item>
          <IgTextField
            label={`Variant ID`}
            value={id}
            onChange={(e) => {
              setId(e.target.value);
            }}
          />
        </Grid>
        <Grid item>
          <IgButton
            onClick={() => {
              if (metafield.data?.metafield?.id) {
                deleteMetafield.mutate(
                  {
                    body: {
                      key: metafield.data?.metafield.key,
                      ownerId: new ShopifyVariantId(id).gid,
                      namespace: "intelligems",
                    },
                    pathParams: undefined,
                    queryParams: undefined,
                  },
                  {
                    onSuccess: () => {
                      void queryClient.invalidateQueries({
                        queryKey: organizationsKeys.metafield(
                          org.data.org.id,
                          // @ts-ignore
                          params
                        ),
                      });
                    },
                  }
                );
              }
            }}
            loading={deleteMetafield.isPending}
            disabled={!valid}
          >
            Delete Metafield
          </IgButton>
        </Grid>
      </Grid>
      <Grid item>{el}</Grid>
    </Grid>
  );
};
